import React from "react";
import {
  Buildings, UserCircleGear, CalendarCheck, Gauge, ScrollIcon, ArrowRight,
} from "@phosphor-icons/react";

const BENEFITS = [
  {
    icon: UserCircleGear,
    title: "A dedicated account manager",
    body: "One point of contact, not a call center. They know your sites, your schedule, and your access quirks.",
  },
  {
    icon: CalendarCheck,
    title: "Guaranteed pickup windows",
    body: "Built around your property's schedule — not ours. Move-out spikes and turnover season planned for in advance.",
  },
  {
    icon: Gauge,
    title: "Capacity that scales with occupancy",
    body: "No scrambling during turnover season or move-out spikes. We flex up — and back down — with your real volume.",
  },
  {
    icon: ScrollIcon,
    title: "Terms structured around your site",
    body: "Number of units, frequency, access constraints, compliance needs. The contract fits the property, not the other way around.",
  },
];

export default function CommercialPricing() {
  return (
    <section
      data-testid="commercial-pricing-section"
      id="commercial-pricing"
      className="bg-white border-b-2 border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-28">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-6">
          <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-emerald-700">
            Commercial &amp; Contract Pricing
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Headline + intro */}
          <div className="lg:col-span-7">
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-[1.05] tracking-tight">
              Built for properties that can&apos;t afford downtime.
            </h2>
            <p className="mt-6 text-slate-700 text-lg leading-relaxed">
              Apartment buildings, property managers and retail stores don&apos;t need a one-off bin
              &mdash; you need a partner who shows up on schedule, every time, without you having to
              think about it.
            </p>

            <div className="mt-8 text-[11px] font-extrabold tracking-[0.22em] uppercase text-slate-700">
              What contract accounts get
            </div>
            <ul className="mt-4 grid sm:grid-cols-2 gap-5">
              {BENEFITS.map((b) => (
                <li key={b.title} className="border-2 border-slate-900 p-5 bg-white">
                  <div className="inline-flex items-center justify-center h-10 w-10 border-2 border-slate-900 bg-emerald-50">
                    <b.icon size={20} weight="bold" className="text-emerald-700" />
                  </div>
                  <div className="font-display font-bold text-base text-slate-900 mt-4">{b.title}</div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.body}</p>
                </li>
              ))}
            </ul>

            <p className="mt-8 text-slate-700 leading-relaxed">
              Every property is different, so every contract is priced around your actual volume and
              service requirements. <span className="font-bold text-slate-900">No generic rate card.
              No guesswork.</span>
            </p>

            <div className="mt-8">
              <a
                href="/commercial-quote"
                data-testid="commercial-cta-primary"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase tracking-[0.14em] text-sm border-2 border-emerald-700 hover:border-emerald-800 transition-colors"
              >
                Request a custom quote
                <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right — who this is for */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="border-2 border-slate-900 bg-slate-900 text-white">
              <div className="border-b-2 border-emerald-500 px-7 py-6">
                <div className="flex items-center gap-3">
                  <Buildings size={26} weight="bold" className="text-emerald-400" />
                  <div className="font-display font-black text-xl">Who this is for</div>
                </div>
              </div>
              <ul className="px-7 py-6 space-y-4 text-sm">
                {[
                  "Apartment buildings & multi-family residential",
                  "Property management companies with multiple sites",
                  "Retail plazas and stores",
                  "Non-profit organizations",
                  "Other commercial properties with recurring needs",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-slate-200">
                    <span className="mt-2 h-1.5 w-1.5 bg-emerald-400 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t-2 border-slate-800 px-7 py-5 text-xs text-slate-400 leading-relaxed">
                One dedicated point of contact. Pricing tailored to your real volume.
                A response within 1&ndash;2 business days.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
